import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from "react";
import { api } from "@/lib/api";
import { getHealthLang, setHealthLang, makeHT } from "@/lib/healthI18n";

const HealthContext = createContext(null);
const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");

export function HealthProvider({ children }) {
  const [overview, setOverview] = useState(null);
  const [tick, setTick] = useState(0);
  const [lang, setLangState] = useState(getHealthLang());
  const wsRef = useRef(null);

  const loadOverview = useCallback(async () => {
    try { const { data } = await api.get("/health/overview"); setOverview(data); } catch {}
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview, tick]);

  useEffect(() => {
    let closed = false, attempts = 0, timer;
    const connect = () => {
      if (closed) return;
      let ws;
      try { ws = new WebSocket(`${WS_BASE}/api/ws`); } catch { return schedule(); }
      wsRef.current = ws;
      ws.onopen = () => { attempts = 0; };
      ws.onmessage = (e) => {
        try { const m = JSON.parse(e.data); if (String(m.type || "").startsWith("HEALTH")) setTick((t) => t + 1); } catch {}
      };
      ws.onclose = () => schedule();
      ws.onerror = () => { try { ws.close(); } catch {} };
    };
    const schedule = () => { if (closed) return; attempts += 1; clearTimeout(timer); timer = setTimeout(connect, Math.min(1000 * attempts, 6000)); };
    connect();
    const ping = setInterval(() => { try { if (wsRef.current?.readyState === 1) wsRef.current.send("ping"); } catch {} }, 25000);
    return () => { closed = true; clearInterval(ping); clearTimeout(timer); try { wsRef.current?.close(); } catch {} };
  }, []);

  const refresh = useCallback(() => setTick((t) => t + 1), []);
  const setLang = useCallback((c) => { setHealthLang(c); setLangState(c); }, []);
  const ht = useMemo(() => makeHT(lang), [lang]);

  return (
    <HealthContext.Provider value={{ overview, tick, refresh, lang, setLang, ht }}>
      {children}
    </HealthContext.Provider>
  );
}

export const useHealth = () => useContext(HealthContext);

// Compose a non-diagnostic AI summary sentence from structured data.
export function buildSummary(analysis, ht) {
  if (!analysis) return "";
  const n = (analysis.signals || []).length;
  if (analysis.risk_level === "stable" || n === 0) return ht("summaryStable");
  return `${ht("summaryLead")} ${n} ${ht("signalsDetected")} — ${ht("risk_" + analysis.risk_level)}. ${ht("summaryTail")}`;
}
